import { NetworkStatus, useQuery } from "@apollo/client";
import { useNavigate, useParams, Routes, Route } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import DocumentationIconButton from "components/DocumentationIconButton";
import NoAccessPage from "components/error/NoAccessPage";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import LoginPolicyManageDropdown from "views/Account/LoginPolicyDetails/ManageDropdown";
import { AccountContext } from "views/AccountWrapper";

import { showLoginPolicyEditDetailsDrawer } from "../LoginPolicyEditDetailsDrawer";
import useTakenPolicyNames from "../Policies/useTakenPolicyNames";
import LoginPolicyDetailsBody from "./Body";
import { GET_POLICY } from "./gql";
import LoginPolicyDetailsSamples from "./Samples";
import { PolicyQueryResponse } from "./types";

const LoginPolicyDetails = () => {
  const { accountName, viewer } = useTypedContext(AccountContext);

  useTitle(`Login policy details · ${accountName}`);

  const params = useParams<{ policyId?: string }>();
  const navigate = useNavigate();

  const { policyId } = params;

  const deleteCallback = () => navigate(`/settings/login-policy`);

  const {
    loading: isPolicyLoading,
    data,
    networkStatus,
    error,
  } = useQuery<PolicyQueryResponse>(GET_POLICY, {
    variables: {
      policyId,
    },
    skip: !policyId,
  });

  useBreadcrumbs(
    [
      {
        title: "Organization settings",
      },
      {
        title: "Login Policy",
        link: "/settings/login-policy",
      },
      {
        title: data?.policy?.name || "",
      },
    ],
    [data?.policy?.name]
  );
  const takenPolicyNames = useTakenPolicyNames();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPolicyLoading && networkStatus !== NetworkStatus.refetch && !data?.policy) {
    return <PageLoading />;
  }

  if (!viewer.admin) {
    return <NoAccessPage />;
  }

  if (!policyId || !data?.policy) {
    return <NotFoundPage />;
  }

  const isLoginPolicyActive = data?.authorizationScheme !== AuthorizationScheme.ManagedUsers;

  const policyUrl = `/login-policy/${data.policy.id}`;
  const policySamplesUrl = `${policyUrl}/samples`;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" align="center" justify="between" fullWidth>
          <ViewHeaderTitle>{data.policy.name}</ViewHeaderTitle>
          <Box gap="large">
            <DocumentationIconButton
              href={getDocsUrl("/concepts/policy/login-policy.html")}
              tooltipText="Go to login policies documentation"
            />
            <LoginPolicyManageDropdown
              id={data.policy.id}
              name={data.policy.name}
              handleLoginPolicyEditDetailsClick={() =>
                showLoginPolicyEditDetailsDrawer({
                  policyId: data.policy.id,
                  name: data.policy.name,
                  labels: data.policy.labels,
                  body: data.policy.body,
                  takenPolicyNames,
                })
              }
              loginPolicyDeleteCallback={deleteCallback}
              buttonVariant="secondary"
              size="medium"
              isLoginPolicyActive={isLoginPolicyActive}
              analyticsPage={AnalyticsPageOrganization.OrganizationLoginPolicyDetails}
            />
          </Box>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <TimestampMetaInfo prefix="Updated on" timestamp={data.policy.updatedAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <Tab end to={policyUrl} label="Policy body" />
          <TabWithCounter count={data.policy.sampleCount} to={policySamplesUrl} label="Samples" />
        </ViewHeaderNavigation>
      </ViewHeader>

      <Routes>
        <Route
          index
          element={
            <LoginPolicyDetailsBody
              key={data.policy.id}
              policy={data.policy}
              initialBody={data.policy.body}
              isLoginPolicyActive={isLoginPolicyActive}
            />
          }
        />
        <Route path="samples" element={<LoginPolicyDetailsSamples id={data.policy.id} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export default LoginPolicyDetails;
